import React, { useState, useEffect } from 'react';
import { 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Send, 
  MessageSquare,
  Bug,
  Zap,
  Shield,
  Database,
  RefreshCw,
  Loader2,
  ChevronDown,
  ExternalLink,
  Settings,
  Lightbulb,
  GraduationCap,
  Link,
  UserPlus,
  Wrench
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icônes par catégorie
const CATEGORY_ICONS = {
  // Bugs
  auth: Shield,
  data_missing: Database,
  ui_broken: AlertCircle,
  api_error: Zap,
  sync_issue: RefreshCw,
  permission: Shield,
  performance: Clock,
  // Demandes
  adaptation: Settings,
  modification: Wrench,
  onboarding: UserPlus,
  formation: GraduationCap,
  integration: Link,
  feature_request: Lightbulb,
  other: MessageSquare
};

// Couleurs par sévérité
const SEVERITY_COLORS = {
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-green-100 text-green-800 border-green-200'
};

// Couleurs par statut
const STATUS_CONFIG = {
  nouveau: { color: 'bg-blue-100 text-blue-800', label: 'Nouveau' },
  auto_fix_en_cours: { color: 'bg-purple-100 text-purple-800', label: 'Réparation auto...' },
  resolu_auto: { color: 'bg-green-100 text-green-800', label: 'Résolu (auto)' },
  escalade: { color: 'bg-red-100 text-red-800', label: 'Escaladé' },
  en_cours: { color: 'bg-orange-100 text-orange-800', label: 'En cours' },
  resolu: { color: 'bg-green-100 text-green-800', label: 'Résolu' }
};

export default function SupportPage() {
  const [categories, setCategories] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    categorie: 'other',
    page_url: window.location.pathname,
    error_message: '',
    steps_to_reproduce: ''
  });

  const token = localStorage.getItem('token');
  const societeId = localStorage.getItem('societeId');

  useEffect(() => {
    fetchCategories();
    fetchMyTickets();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/support-tickets/categories`);
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    }
  };

  const fetchMyTickets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/support-tickets/my-tickets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Societe-Id': societeId
        }
      });
      const data = await response.json();
      setMyTickets(data.tickets || []);
    } catch (error) {
      console.error('Erreur chargement tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccessMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/support-tickets/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Societe-Id': societeId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(data);
        setFormData({
          titre: '',
          description: '',
          categorie: 'other',
          page_url: window.location.pathname,
          error_message: '',
          steps_to_reproduce: ''
        });
        setShowForm(false);
        fetchMyTickets();
      }
    } catch (error) {
      console.error('Erreur soumission:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="support-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-green-600" />
            Support & Assistance
          </h1>
          <p className="text-gray-600 mt-2">
            Signalez un problème ou suivez vos demandes d'assistance
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div 
            className={`mb-6 p-4 rounded-lg border ${
              successMessage.statut === 'resolu_auto' 
                ? 'bg-green-50 border-green-200' 
                : successMessage.statut === 'escalade'
                ? 'bg-orange-50 border-orange-200'
                : 'bg-blue-50 border-blue-200'
            }`}
            data-testid="success-message"
          >
            <div className="flex items-start gap-3">
              {successMessage.statut === 'resolu_auto' ? (
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
              )}
              <div>
                <p className="font-medium text-gray-900">{successMessage.message}</p>
                {successMessage.auto_fix?.message && (
                  <p className="text-sm text-gray-600 mt-1">
                    Action effectuée : {successMessage.auto_fix.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* New Ticket Buttons */}
        {!showForm && (
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => { setShowForm(true); setFormData({...formData, formType: 'bug'}); }}
              className="flex items-center gap-3 p-4 bg-white border-2 border-red-200 rounded-xl hover:border-red-400 hover:bg-red-50 transition-all"
              data-testid="new-bug-btn"
            >
              <div className="p-3 bg-red-100 rounded-lg">
                <Bug className="w-6 h-6 text-red-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-900">Signaler un problème</p>
                <p className="text-sm text-gray-500">Bug, erreur, dysfonctionnement</p>
              </div>
            </button>
            
            <button
              onClick={() => { setShowForm(true); setFormData({...formData, formType: 'request'}); }}
              className="flex items-center gap-3 p-4 bg-white border-2 border-blue-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all"
              data-testid="new-request-btn"
            >
              <div className="p-3 bg-blue-100 rounded-lg">
                <Lightbulb className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-900">Faire une demande</p>
                <p className="text-sm text-gray-500">Adaptation, onboarding, formation</p>
              </div>
            </button>
          </div>
        )}

        {/* New Ticket Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8" data-testid="ticket-form">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              {formData.formType === 'bug' ? (
                <>
                  <Bug className="w-5 h-5 text-red-500" />
                  Signaler un problème technique
                </>
              ) : (
                <>
                  <Lightbulb className="w-5 h-5 text-blue-500" />
                  Faire une demande
                </>
              )}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Catégorie - Bugs */}
              {formData.formType === 'bug' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de problème
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {categories.filter(cat => cat.type === 'bug').map((cat) => {
                      const Icon = CATEGORY_ICONS[cat.id] || Bug;
                      const isSelected = formData.categorie === cat.id;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, categorie: cat.id })}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            isSelected
                              ? 'border-red-500 bg-red-50 ring-2 ring-red-200'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          data-testid={`category-${cat.id}`}
                        >
                          <Icon className={`w-5 h-5 mb-1 ${isSelected ? 'text-red-600' : 'text-gray-400'}`} />
                          <p className={`text-sm font-medium ${isSelected ? 'text-red-700' : 'text-gray-700'}`}>
                            {cat.description.split('/')[0].trim()}
                          </p>
                          {cat.auto_fix && (
                            <span className="text-xs text-green-600">Auto-réparation</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Catégorie - Demandes */}
              {formData.formType === 'request' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de demande
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {categories.filter(cat => cat.type === 'request').map((cat) => {
                      const Icon = CATEGORY_ICONS[cat.id] || Lightbulb;
                      const isSelected = formData.categorie === cat.id;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, categorie: cat.id })}
                          className={`p-4 rounded-lg border text-left transition-all flex items-start gap-3 ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          data-testid={`category-${cat.id}`}
                        >
                          <Icon className={`w-6 h-6 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                          <div>
                            <p className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                              {cat.description}
                            </p>
                            {cat.id === 'adaptation' && (
                              <p className="text-xs text-gray-500 mt-1">Personnalisation pour votre exploitation</p>
                            )}
                            {cat.id === 'onboarding' && (
                              <p className="text-xs text-gray-500 mt-1">Configuration initiale, prise en main</p>
                            )}
                            {cat.id === 'formation' && (
                              <p className="text-xs text-gray-500 mt-1">Formation à distance ou sur site</p>
                            )}
                            {cat.id === 'integration' && (
                              <p className="text-xs text-gray-500 mt-1">Connexion avec vos autres outils</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Titre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.formType === 'bug' ? 'Titre du problème *' : 'Objet de votre demande *'}
                </label>
                <input
                  type="text"
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  placeholder={formData.formType === 'bug' 
                    ? "Ex: Impossible d'accéder à mes interventions" 
                    : "Ex: Adaptation pour ma cave viticole"}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                  data-testid="ticket-title"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.formType === 'bug' ? 'Description du problème *' : 'Décrivez votre besoin *'}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={formData.formType === 'bug'
                    ? "Décrivez le problème que vous rencontrez..."
                    : "Expliquez-nous votre besoin en détail : votre activité, ce que vous souhaitez..."}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                  data-testid="ticket-description"
                />
              </div>

              {/* Champs spécifiques aux bugs */}
              {formData.formType === 'bug' && (
                <>
                  {/* Message d'erreur (optionnel) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message d'erreur (si affiché)
                    </label>
                    <input
                      type="text"
                      value={formData.error_message}
                      onChange={(e) => setFormData({ ...formData, error_message: e.target.value })}
                      placeholder="Ex: 404 Not Found, Erreur serveur..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      data-testid="ticket-error"
                    />
                  </div>

                  {/* Étapes pour reproduire */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Comment reproduire le problème ?
                    </label>
                    <textarea
                      value={formData.steps_to_reproduce}
                      onChange={(e) => setFormData({ ...formData, steps_to_reproduce: e.target.value })}
                      placeholder="1. Je clique sur...&#10;2. Je vais sur...&#10;3. L'erreur apparaît"
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      data-testid="ticket-steps"
                    />
                  </div>
                </>
              )}

              {/* Champs spécifiques aux demandes */}
              {formData.formType === 'request' && (
                <>
                  {/* Type d'exploitation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type d'exploitation (optionnel)
                    </label>
                    <input
                      type="text"
                      value={formData.type_exploitation || ''}
                      onChange={(e) => setFormData({ ...formData, type_exploitation: e.target.value })}
                      placeholder="Ex: Viticulture, Grandes cultures, Élevage bovin..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Délai souhaité */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Délai souhaité
                    </label>
                    <select
                      value={formData.delai || ''}
                      onChange={(e) => setFormData({ ...formData, delai: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Pas de délai particulier</option>
                      <option value="urgent">Urgent (cette semaine)</option>
                      <option value="normal">Normal (ce mois-ci)</option>
                      <option value="flexible">Flexible (pas pressé)</option>
                    </select>
                  </div>
                </>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                    formData.formType === 'bug' 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                  data-testid="submit-ticket-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                  {submitting ? 'Envoi en cours...' : 'Envoyer'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  data-testid="cancel-btn"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* My Tickets */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200" data-testid="my-tickets">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Mes demandes d'assistance
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
              <p className="text-gray-500 mt-2">Chargement...</p>
            </div>
          ) : myTickets.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-gray-600">Aucune demande en cours</p>
              <p className="text-sm text-gray-400 mt-1">
                Vous n'avez signalé aucun problème pour le moment
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {myTickets.map((ticket) => {
                const statusConfig = STATUS_CONFIG[ticket.statut] || STATUS_CONFIG.nouveau;
                const Icon = CATEGORY_ICONS[ticket.categorie] || Bug;
                
                return (
                  <div 
                    key={ticket.id} 
                    className="p-4 hover:bg-gray-50 transition-colors"
                    data-testid={`ticket-${ticket.numero}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`p-2 rounded-lg ${SEVERITY_COLORS[ticket.severite] || 'bg-gray-100'}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-mono text-gray-500">
                              #{ticket.numero}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                              {statusConfig.label}
                            </span>
                          </div>
                          <h3 className="font-medium text-gray-900 truncate">
                            {ticket.titre}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {ticket.description}
                          </p>
                          {ticket.resolution && (
                            <div className="mt-2 p-2 bg-green-50 rounded-lg">
                              <p className="text-sm text-green-700">
                                <strong>Résolution :</strong> {ticket.resolution}
                              </p>
                            </div>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            {formatDate(ticket.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
          <h3 className="font-semibold text-green-800 mb-2">
            Besoin d'aide immédiate ?
          </h3>
          <p className="text-sm text-green-700 mb-4">
            Notre système tente de réparer automatiquement les problèmes courants.
            Pour les problèmes critiques, notre équipe est alertée immédiatement.
          </p>
          <div className="flex flex-wrap gap-3">
            <a 
              href="/aide" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-green-700 rounded-lg border border-green-200 hover:bg-green-50 transition-colors text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Guide d'utilisation
            </a>
            <a 
              href="/assistant" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              <MessageSquare className="w-4 h-4" />
              Parler à l'assistant IA
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
